import {
  Content,
  DialogContainer,
  Dialog as SpectrumDialog,
} from '@adobe/react-spectrum';
import { Icon } from '@components/Icon';
import { ModalHeader } from '@components/styled';
import { CSSObject, css } from '@emotion/react';
import { useFlagMe214355SpectrumUpgradeDialog } from '@generated/flags/ME-214355-spectrum-upgrade-dialog';
import { useTheme } from '@hooks/useTheme';
import {
  DialogContent,
  DialogContentProps,
  DialogOverlay,
} from '@reach/dialog';
import { PropsWithAs } from '@reach/utils';
import { HEADER_HEIGHT } from '@utils/constants';
import { MODAL_BACKGROUND, MODAL_CONTENT } from '@utils/zIndex';
import { uniqueId } from 'lodash-es';
import { CSSProperties, FC, HTMLProps, createContext, useRef } from 'react';
import { SaveButton } from '../Button/SaveButton';
import { Padding } from '../Padding';

const activeStyle = css({
  display: 'flex',
});

const modalBackground: CSSObject = {
  position: 'fixed',
  zIndex: MODAL_BACKGROUND,
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  alignItems: 'center',
  display: 'none',
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'hidden',
  paddingTop: HEADER_HEIGHT,
};

const modalContent: CSSObject = {
  position: 'relative',
  minHeight: 'auto',
  padding: '3rem',
  borderRadius: '3px',
  width: 640,
  maxHeight: 'calc(100vh - 3.5rem)',
  overflow: 'visible',
};

const modalContentV2: CSSObject = {
  width: '100%',
  minHeight: 'auto',
  maxHeight: '100%',
  padding: '1rem',
  borderRadius: '3px',
};

export interface Props {
  active: boolean;
  /** @deprecated Use `onClose` prop */
  closeModal?: () => void;
  onClose?: () => void;
  confirmationText?: string;
  onConfirmDisabled?: boolean;
  confirmButtonEnabled?: boolean;
  id: string;
  onConfirm?: () => void;
  style?: CSSObject;
  className?: string;
  contentStyle?: CSSProperties;
  dialogContentProps?: PropsWithAs<'div', DialogContentProps>;
  /** @deprecated This should not be used. Contact Atlas. */
  forcev2?: boolean;
  /** @deprecated This should not be used. Contact Atlas. */
  forcev1?: boolean;
  size?: 'S' | 'M' | 'L';
}

type CloseButtonProps = HTMLProps<HTMLButtonElement>;

// ts-unused-exports:disable-next-line
export const CloseButton: FC<CloseButtonProps> = (props: CloseButtonProps) => {
  return (
    <button
      css={{
        // eslint-disable-next-line mastery/named-colors
        color: '#363636',
        position: 'absolute',
        right: '.5rem',
        fontSize: '1.25rem',
        top: '1rem',
        padding: '5px 10px',
        overflow: 'auto',
        zIndex: MODAL_CONTENT,
      }}
      {...props}
      data-dialog-close
      data-testid="dialog-close"
      aria-label="Dismiss"
      type="button"
    >
      <Icon i="x" size="lg" color="text" />
    </button>
  );
};

export const IsInDialogContext = createContext(false);

export const Dialog: FC<Props> = ({
  active: isActive,
  closeModal: closeModalProp,
  onClose,
  confirmationText = 'Save',
  onConfirmDisabled = false,
  confirmButtonEnabled = true,
  forcev2 = false,
  forcev1 = false,
  id,
  onConfirm,
  style,
  children,
  className,
  contentStyle,
  dialogContentProps,
  size,
}) => {
  const { dialog } = useTheme();
  const contextBtnId = useRef(uniqueId());
  const spectrumUpgradeDialog = useFlagMe214355SpectrumUpgradeDialog();

  const closeModal = (): void => {
    onClose?.();
    closeModalProp?.();
  };

  const shouldShowConfirmButton = onConfirm && confirmButtonEnabled;

  if ((spectrumUpgradeDialog || forcev2) && !forcev1) {
    return (
      <IsInDialogContext.Provider value>
        <DialogContainer
          onDismiss={(): void => {
            closeModal();
          }}
        >
          {isActive && (
            <div className={className} css={{ maxWidth: '100%', ...style }}>
              <SpectrumDialog
                role="dialog"
                data-testid={'dialog'}
                id={`${id}-dialog`}
                data-isOpen={isActive}
                aria-label="modal"
                data-modalactive={isActive}
                data-active={isActive}
                size={size || 'L'}
              >
                <div
                  data-testid="dialog-content"
                  css={{
                    ...modalContentV2,
                    ...dialog.content,
                    backgroundColor: 'inherit',
                  }}
                  style={contentStyle}
                >
                  <Content
                    aria-label="modal"
                    UNSAFE_style={{
                      height: '100%',
                    }}
                  >
                    <CloseButton onClick={() => closeModal()} />
                    <div
                      css={{
                        height: shouldShowConfirmButton
                          ? 'calc(100% - 36px)'
                          : '100%',
                      }}
                      {...dialogContentProps}
                    >
                      {children}
                      {shouldShowConfirmButton && (
                        <Padding t2 css={{ textAlign: 'center' }}>
                          <SaveButton
                            data-testid="dialog-confirm"
                            css={{ margin: '0 auto', display: 'block' }}
                            disabled={(): boolean => onConfirmDisabled}
                            onClick={onConfirm}
                            id={`dialog-confirm-${contextBtnId.current || 0}`}
                          >
                            {confirmationText}
                          </SaveButton>
                        </Padding>
                      )}
                    </div>
                  </Content>
                </div>
              </SpectrumDialog>
            </div>
          )}
        </DialogContainer>
      </IsInDialogContext.Provider>
    );
  }
  return (
    <IsInDialogContext.Provider value>
      <DialogOverlay
        css={[modalBackground, isActive && activeStyle, dialog.overlay, style]}
        className={className}
        data-testid="dialog"
        id={`${id}-dialog`}
        isOpen={isActive}
        aria-label="modal"
        data-modalactive={isActive}
        onDismiss={closeModal}
      >
        <DialogContent
          aria-label="modal"
          css={{ ...modalContent, ...dialog.content, ...contentStyle }}
          {...dialogContentProps}
        >
          <CloseButton onClick={closeModal} />
          {children}
          {shouldShowConfirmButton && (
            <Padding t2 css={{ textAlign: 'center' }}>
              <SaveButton
                data-testid="dialog-confirm"
                css={{ margin: '0 auto', display: 'block' }}
                disabled={(): boolean => onConfirmDisabled}
                onClick={onConfirm}
                id={`dialog-confirm-${contextBtnId.current || 0}`}
              >
                {confirmationText}
              </SaveButton>
            </Padding>
          )}
        </DialogContent>
      </DialogOverlay>
    </IsInDialogContext.Provider>
  );
};

export const DialogTitle = ModalHeader;
